import {inject, InjectionToken} from '@angular/core';
import {CanActivateFn, Router} from '@angular/router';
import {config} from '../../config';
import {AppService} from '../services/app.service';
import {ReferralSourceService} from '../services/referral-source.service';
import {appendAppStateParams} from '../pipes/append-app-state-params.pipe';

/**
 * Loads a page outside the app. Injected rather than called inline so specs can observe the exit:
 * a real one unloads Karma's page and aborts the whole run.
 */
export const LeaveApp = new InjectionToken<(url: string, options: { replace: boolean }) => void>('LeaveApp', {
  providedIn: 'root',
  factory: () => (url, {replace}) => replace ? location.replace(url) : location.assign(url),
});

/**
 * A referral visit only books, so it never enters the finder. Whatever sends it there -- a "View
 * in-network clinicians" button, a BackToFinder dialog action, a booking guard's redirect, the `**`
 * fallback -- returns the patient to the referral's main-site page instead, with the chosen insurance
 * as bridging params. The main site's home page stands in when the visit did not come from a
 * main-site page.
 *
 * A route guard rather than a check in NavService.goto, because the booking guards' RedirectCommands
 * never call goto.
 */
export const referralVisitExitGuard: CanActivateFn = () => {
  const referral = inject(ReferralSourceService);
  if (!referral.isReferral()) {
    return true;
  }
  const url = appendAppStateParams(referral.returnUrl() ?? config.endpoints.mainSite, inject(AppService).data().insurance);
  inject(LeaveApp)(url, {replace: !!inject(Router).currentNavigation()?.extras.replaceUrl});
  return false;
};
